import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MapDeleteComponent } from 'src/app/pages/maplist/map-delete/map-delete.component';
import { MapService } from 'src/app/services/map.service';
import { SubscriptionStatus } from '../../../models/subscription-usage.model';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { Router } from '@angular/router';
import { GameDeleteConfirmationComponent } from '../game-delete-confirmation/game-delete-confirmation.component';
import { MapDeleteInnerConfirmationComponent } from '../map-delete-inner-confirmation/map-delete-inner-confirmation.component';
import { InnerCommunicationDataPassService } from '../../services/inner-communication-data-pass.service';
import { DowngradeStatusResponse } from '../../models/downgrade-response.model';

@Component({
    selector: 'app-map-delete-confirmation',
    templateUrl: './map-delete-confirmation.component.html',
    styleUrls: ['./map-delete-confirmation.component.scss'],
    standalone: false
})
export class MapDeleteConfirmationComponent implements OnInit {
  upgradePlan: any;
  currentPlan: any;
  planUsage: SubscriptionStatus;
  userId: any;
  currentMapsIds: any[];
  mapsToDelete: any[] = [];
  downgradeResponse!: DowngradeStatusResponse;

  constructor(
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private router: Router,
    private mapService: MapService,
    private currentUserService: CurrentUserService,
    private innerCommunicationDataPassService: InnerCommunicationDataPassService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.getMapList();
    this.setupCurrentUserPlanUsage();
  }

  get maxMaps(): number {
    return this.downgradeResponse?.limits_comparison?.maps?.new_plan_limit ?? (this.upgradePlan?.features?.maps_uploaded || 0);
  }

  get mapNamesToDelete(): string {
    return this.mapsToDelete.map(m => m.map_name).join(', ');
  }

  getMapList() {
    this.mapService.mapList$.subscribe((res: any) => {
      if (res) {
        let userMaps = res.filter((map: any) => map.user_id === this.userId);
        this.currentMapsIds = userMaps.map((m: any) => m.map_id);

        let allowed = this.maxMaps;
        if (allowed === -1) {
          this.mapsToDelete = [];
        } else if (userMaps.length > allowed) {
          userMaps.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          this.mapsToDelete = userMaps.slice(allowed);
        } else {
          this.mapsToDelete = [];
        }
      }
      else {
        this.mapService.getMapList();
      }
      this.cdr.detectChanges();
    })
  }

  setupCurrentUserPlanUsage() {
    this.currentUserService.planUsageData.subscribe((data: SubscriptionStatus) => {
      if (data) {
        this.planUsage = data;
        this.cdr.detectChanges();
      }
    });
  }

  getPlacedContent(map: any): number {
    return (map.adventures || 0) + (map.establishments || 0) + (map.city_establishments || 0) + (map.city_adventures || 0);
  }

  close() {
    this.activeModal.close();
    this.innerCommunicationDataPassService.childModalCloseEvent(true);
  }

  deleteAll() {
    const modalRef = this.modalService.open(MapDeleteComponent, {
      size: 'md',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.mapId = null;
    modalRef.componentInstance.mapIds = this.currentMapsIds;
    modalRef.componentInstance.userId = this.userId;

    modalRef.result.then((result) => {
      if (result === 'deleted') {
        this.mapService.getMapList(this.userId, true)
      }
    
          this.cdr.detectChanges();});
  }

  deleteMaps() {
    this.activeModal.close('map-delete')
    this.router.navigate(['maplist'])
  }

  async next() {
    // Sequentially confirm each map
    for (const map of this.mapsToDelete) {
      const confirmed = await this.openInnerConfirmation(map.map_name);
      if (!confirmed) {
        // If aborted or exited, do not continue the flow
        return;
      }
    }
    
    // Once confirmed, proceed to games
    this.openGameDelete();
  }

  private openInnerConfirmation(mapName: string): Promise<boolean> {
    const modalRef = this.modalService.open(MapDeleteInnerConfirmationComponent, {
      backdrop: 'static',
      centered: true,
      keyboard: false
    });
    modalRef.componentInstance.mapName = mapName;
    return modalRef.result.then(
      (res) => res === 'proceed',
      () => false
    );
  }

  private openGameDelete() {
    this.activeModal.close('next');
    const modalRef = this.modalService.open(GameDeleteConfirmationComponent, {
      size: 'md',
      backdrop: 'static',
      centered: true,
      keyboard: false
    });
    modalRef.componentInstance.userId = this.userId;
    modalRef.componentInstance.upgradePlan = this.upgradePlan;
    modalRef.componentInstance.currentPlan = this.currentPlan;
    modalRef.componentInstance.deletedMapIds = this.mapsToDelete.map(m => m.map_id);
    modalRef.componentInstance.downgradeResponse = this.downgradeResponse;

    modalRef.result.then((result) => {
      if (result === 'deleted') {
        this.mapService.getMapList(this.userId, true)
      }
      else if (result === 'close') {
        this.activeModal.close('close');
      }
    
          this.cdr.detectChanges();});
  }

  subscribe() {
    this.activeModal.close('subscribe')
    this.router.navigate(['/subscription'])
  }
}

